import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import HawkIcon from '../components/ui/HawkIcon';
import { useAuth } from '../contexts/AuthContext';
import styles from './LoginPage.module.css';

const DEFAULT_NEXT_REQUEST_SECONDS = 60;

function applyOtpCooldownFromResponse(setOtpReadyAt, setNowTick, data) {
  const seconds = data?.nextRequestAfterSeconds ?? DEFAULT_NEXT_REQUEST_SECONDS;
  setOtpReadyAt(Date.now() + seconds * 1000);
  setNowTick(Date.now());
}

function applyOtpCooldownFromError(setOtpReadyAt, setNowTick, setError, err) {
  const retryAfterSeconds = err.response?.data?.retryAfterSeconds;
  if (retryAfterSeconds) {
    setOtpReadyAt(Date.now() + retryAfterSeconds * 1000);
    setNowTick(Date.now());
  }
  const message =
    err.response?.data?.message ||
    err.response?.data?.errors?.[0]?.msg ||
    'Could not send verification code.';
  setError(message);
}

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState('admin');
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [otpReadyAt, setOtpReadyAt] = useState(0);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const { requestOtp, verifyOtp, loading, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const roleParam = searchParams.get('tab');
    if (roleParam === 'staff') {
      setRole('staff');
    } else if (roleParam === 'admin') {
      setRole('admin');
    }
  }, [searchParams]);

  useEffect(() => {
    if (otpReadyAt <= Date.now()) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [otpReadyAt]);

  const otpSecondsLeft = Math.max(0, Math.ceil((otpReadyAt - nowTick) / 1000));
  const otpCooldownActive = otpSecondsLeft > 0;

  const resetFlow = () => {
    setStep('email');
    setEmail('');
    setCode('');
    setError('');
    setOtpReadyAt(0);
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    resetFlow();
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    if (otpCooldownActive || loading) return;
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    try {
      const data = await requestOtp(email.trim(), role);
      setStep('otp');
      setCode('');
      applyOtpCooldownFromResponse(setOtpReadyAt, setNowTick, data);
    } catch (err) {
      applyOtpCooldownFromError(setOtpReadyAt, setNowTick, setError, err);
    }
  };

  const handleResend = async () => {
    if (otpCooldownActive || loading) return;
    setError('');
    try {
      const data = await requestOtp(email.trim(), role);
      applyOtpCooldownFromResponse(setOtpReadyAt, setNowTick, data);
    } catch (err) {
      applyOtpCooldownFromError(setOtpReadyAt, setNowTick, setError, err);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    try {
      const userData = await verifyOtp(email.trim(), trimmed, role);
      const redirectPath = userData.role === 'admin' ? '/admin' : '/staff';
      navigate(redirectPath);
    } catch (err) {
      let errorMessage = 'Verification failed. Please try again.';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.code === 'NETWORK_ERROR' || err.message?.includes('Network Error')) {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      setError(errorMessage);
    }
  };

  if (loading && !isAuthenticated && step === 'email' && !email) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    const redirectPath = user?.role === 'staff' ? '/staff' : '/admin';
    return <Navigate to={redirectPath} replace />;
  }

  const sendButtonLabel = loading
    ? 'Sending...'
    : otpCooldownActive
      ? `Send verification code (${otpSecondsLeft}s)`
      : 'Send verification code';

  return (
    <>
      <div className={styles.loginRoot}>
        <div className={styles.loginContainer}>
          <div className={styles.headerSection}>
            <div className={styles.branding}>
              <HawkIcon size={56} className={styles.brandMark} />
              <p className={styles.brandSubtitle}>
                Event check-in &amp; participant management
              </p>
            </div>
            <p className={styles.description}>
              QR code check-in, RSVP flows, real-time analytics, and a smooth experience for organizers and attendees.
            </p>
          </div>

          <div className={styles.loginCard}>
            <div className={styles.cardHeader}>
              <h3 className={styles.welcomeTitle}>
                Welcome Back
              </h3>
              <p className={styles.welcomeSubtitle}>
                {step === 'email'
                  ? 'Sign in with a one-time code sent to your email'
                  : 'Enter the code we sent you'}
              </p>
            </div>

            <div className={styles.roleSwitcher}>
              <button
                type="button"
                className={`${styles.roleButton} ${role === 'admin' ? styles.roleButtonActive : ''}`}
                onClick={() => handleRoleChange('admin')}
                disabled={loading}
              >
                <span className="material-symbols-outlined">admin_panel_settings</span>
                <span>Admin</span>
              </button>
              <button
                type="button"
                className={`${styles.roleButton} ${role === 'staff' ? styles.roleButtonActive : ''}`}
                onClick={() => handleRoleChange('staff')}
                disabled={loading}
              >
                <span className="material-symbols-outlined">qr_code_scanner</span>
                <span>Staff</span>
              </button>
            </div>

            {error && (
              <div className={styles.errorAlert}>
                <span className="material-symbols-outlined">error</span>
                <div>
                  <p className={styles.errorTitle}>
                    {step === 'email' ? 'Could not continue' : 'Verification failed'}
                  </p>
                  <p className={styles.errorMessage}>
                    {error}
                  </p>
                </div>
                <button
                  className={styles.errorClose}
                  onClick={() => setError('')}
                  type="button"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
            )}

            {step === 'email' ? (
              <form onSubmit={handleSendCode} className={styles.loginForm}>
                <Input
                  label="Email Address *"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="you@organization.com"
                  fullWidth
                  startIcon={<span className="material-symbols-outlined">mail</span>}
                  error={error && !email.trim()}
                  disabled={loading}
                  autoComplete="email"
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || otpCooldownActive}
                  startIcon={
                    loading ? (
                      <span className="material-symbols-outlined">hourglass_empty</span>
                    ) : (
                      <span className="material-symbols-outlined">mark_email_read</span>
                    )
                  }
                  className={styles.submitButton}
                >
                  {sendButtonLabel}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerify} className={styles.loginForm}>
                <p className={styles.systemInfoText} style={{ marginBottom: '1rem', textAlign: 'left' }}>
                  Code sent to <strong>{email}</strong>
                  {' '}
                  <button
                    type="button"
                    className={styles.forgotPassword}
                    style={{ display: 'inline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => {
                      resetFlow();
                    }}
                  >
                    Change email
                  </button>
                </p>

                <Input
                  label="6-digit code *"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(v);
                    setError('');
                  }}
                  placeholder="000000"
                  fullWidth
                  startIcon={<span className="material-symbols-outlined">pin</span>}
                  error={!!error && code.length > 0}
                  disabled={loading}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <span className="material-symbols-outlined">hourglass_empty</span>
                    ) : (
                      <span className="material-symbols-outlined">login</span>
                    )
                  }
                  className={styles.submitButton}
                >
                  {loading ? 'Verifying...' : 'Verify and sign in'}
                </Button>

                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  <Button
                    type="button"
                    variant="outlined"
                    fullWidth
                    size="medium"
                    disabled={loading || otpCooldownActive}
                    onClick={handleResend}
                  >
                    {otpCooldownActive ? `Resend code (${otpSecondsLeft}s)` : 'Resend code'}
                  </Button>
                </div>
              </form>
            )}

            <div className={styles.systemInfo}>
              <div className={styles.systemInfoDivider}></div>
              <span className={styles.systemInfoLabel}>System Info</span>
              <div className={styles.systemInfoDivider}></div>
            </div>
            <p className={styles.systemInfoText}>
              Sign-in uses a one-time email code (valid for 5 minutes). No password required on this screen.
            </p>
          </div>
        </div>

        <div className={styles.gradientBlur1}></div>
        <div className={styles.gradientBlur2}></div>
      </div>
    </>
  );
};

export default LoginPage;
